// "Playing suggestion" voicings: a plain analyzed chord turned into a voicing that lets a drone ring over it —
// the open B and E strings, or D and G held at the 3rd fret (the Cadd9 / Dsus4 / Em7 family).
// The fretting hand plays the analyzed chord; colour tones (9, sus4/11, 6, 7) come only from the drone or other
// open strings, so nothing is claimed about the recording. Pure, no DOM. Frets are [s6..s1], -1 muted, 0 open.

import { NOTE_NAMES, OPEN_MIDI, chordShape, scalePitchClasses } from "./chords";
import { isBarre, pitchClass, type KeyQuality } from "./transpose";

export interface Voicing { frets: number[]; name: string }
export type DroneId = "open" | "fret3";
interface Key { tonic: string; quality: KeyQuality }

/** drone frets per string [s6..s1], -1 = not part of the drone */
export const DRONES: Record<DroneId, number[]> = {
  open: [-1, -1, -1, -1, 0, 0],    // open B and E ring (E / A / D-major songs)
  fret3: [-1, -1, -1, -1, 3, 3],   // D and G under the 3rd and 4th fingers (G / C-major songs)
};

// analyzed qualities a suggestion is made for; dim / aug / m7b5 / mM7 are left as analyzed
const QUALITY_TONES: Record<string, number[]> = {
  "": [0, 4, 7], m: [0, 3, 7], 7: [0, 4, 7, 10], m7: [0, 3, 7, 10], maj7: [0, 4, 7, 11],
  6: [0, 4, 7, 9], m6: [0, 3, 7, 9], sus2: [0, 2, 7], sus4: [0, 5, 7],
};
const COLORS = [2, 5, 9, 10, 11];   // 9, 4/11, 6, b7, maj7 above the root — the only tones a drone may add
const MAX_FRET = 12;
const MAX_SPAN = 3;          // fretted notes within a 4-fret span
const MAX_FINGERS = 4;
const MAX_COLORS = 2;
const MUDDY_BELOW = 52;      // E3: no 2nds below it (a 9th crammed against the bass)
const DEFAULT_LIMIT = 3;

const mod12 = (x: number) => ((x % 12) + 12) % 12;
const midiAt = (i: number, fret: number) => OPEN_MIDI[6 - i] + fret;   // i = index into [s6..s1]

/** the key the shapes are played in: the song key moved by chordView's shift (transpose − capo) */
export function shapeKey(key: Key, shift: number): Key {
  const pc = pitchClass(key.tonic);
  return pc < 0 ? key : { tonic: NOTE_NAMES[mod12(pc + shift)], quality: key.quality };
}

/** chord suffix for a set of intervals above the root; null when the set is outside the suggestion vocabulary
 *  (b9 / #11 / b13, both thirds, a 4th a semitone above the 3rd, 6 with b7, two sevenths). */
function chordSuffix(s: Set<number>): string | null {
  const has = (i: number) => s.has(i);
  if (!has(0) || has(1) || has(6) || has(8)) return null;
  if ((has(3) && has(4)) || (has(10) && has(11)) || (has(9) && has(10))) return null;
  const seventh = has(10) ? "7" : has(11) ? "maj7" : "";
  const nine = has(2), eleven = has(5), six = has(9);
  if (has(4)) {
    if (eleven) return null;                       // drop the 3rd instead → sus4
    if (seventh) return six ? null : nine ? (seventh === "7" ? "9" : "maj9") : seventh;
    return six ? (nine ? "6add9" : "6") : nine ? "add9" : "";
  }
  if (has(3)) {
    if (seventh === "maj7") return nine || eleven || six ? null : "mM7";
    if (seventh) return six ? null : eleven ? "m11" : nine ? "m9" : "m7";
    if (eleven) return nine || six ? null : "madd11";
    return six ? (nine ? "m6add9" : "m6") : nine ? "madd9" : "m";
  }
  if (eleven) {                                    // no 3rd: sus4, or 11 (7sus4) with the b7
    if (seventh === "7") return six ? null : "11";
    return seventh || six || nine ? null : "sus4";
  }
  return nine && !seventh && !six ? "sus2" : null;
}

/** name a fingering from its sounding notes, rooted on the bass: [x,4,6,4,0,0] → "C#m7"; null if unnameable */
export function nameVoicing(frets: number[]): string | null {
  const b = frets.findIndex((f) => f >= 0);
  if (b < 0) return null;
  const root = mod12(midiAt(b, frets[b]));
  const ivs = new Set(frets.flatMap((f, i) => (f >= 0 ? [mod12(midiAt(i, f) - root)] : [])));
  const suffix = chordSuffix(ivs);
  return suffix == null ? null : NOTE_NAMES[root] + suffix;
}

/** fingers a fingering needs: notes on the lowest fret share the index when it can lie across them from the
 *  lowest fretted string up; null for a full barre (5–6 strings), which a suggestion never asks for */
export function fingersNeeded(frets: number[]): number | null {
  const fretted = frets.filter((f) => f > 0);
  if (!fretted.length) return 0;
  const low = Math.min(...fretted);
  const atLow = frets.flatMap((f, i) => (f === low ? [i] : []));
  const a = atLow[0], z = atLow[atLow.length - 1];
  const barre = atLow.length >= 2 && a === frets.findIndex((f) => f > 0) && frets.slice(a, z + 1).every((f) => f >= low);
  if (barre && z - a + 1 >= 5) return null;
  return (barre ? 1 : atLow.length) + fretted.length - atLow.length;
}

interface Variant { tones: Set<number>; required: number[]; sus: boolean }

// the analyzed chord, plus — for a major 3rd — its sus4 form, used only when a drone 4th forces the 3rd out
function variants(tones: number[]): Variant[] {
  const out: Variant[] = [{ tones: new Set(tones), required: tones.filter((i) => i !== 7), sus: false }];
  if (tones.includes(4)) {
    const sus = tones.map((i) => (i === 4 ? 5 : i));
    out.push({ tones: new Set(sus), required: sus, sus: true });   // a sus chord keeps its 5th
  }
  return out;
}

interface Ranked extends Voicing { score: number }

function rank(label: string, key: Key, droneId: DroneId): Ranked[] {
  const m = /^([A-G]#?)(.*)$/.exec(label);
  const tones = m ? QUALITY_TONES[m[2]] : undefined;
  if (!m || !tones) return [];
  const root = NOTE_NAMES.indexOf(m[1]);
  const drone = DRONES[droneId];
  const scale = scalePitchClasses(key);
  if (!droneFits(drone, scale)) return [];
  const standard = chordShape(label)?.frets ?? [];
  const standardClosed = !standard.includes(0) || isBarre(standard);
  const analyzed = new Set(tones);
  const found = new Map<string, Ranked>();

  for (const v of variants(tones)) {
    // colours must sit in the key's scale; over a major 3rd the 4th only appears through the sus variant
    const colors = new Set(COLORS.filter((i) => !v.tones.has(i) && scale.has(mod12(root + i)) && !(i === 5 && v.tones.has(4))));
    const allowed = (i: number, f: number) => {
      const iv = mod12(midiAt(i, f) - root);
      return v.tones.has(iv) || colors.has(iv);
    };
    const frets = new Array<number>(6).fill(-1);
    const walk = (i: number, lo: number, hi: number) => {
      if (i === 6) {
        const r = evaluate(frets, root, v, analyzed, drone, standard, standardClosed);
        const k = frets.join(",");
        if (r && !found.has(k)) found.set(k, { frets: frets.slice(), ...r });
        return;
      }
      if (i === 5) { frets[i] = -1; walk(i + 1, lo, hi); }   // only the top string may be left muted
      for (let f = 0; f <= MAX_FRET; f++) {
        if (f > 0 && Math.max(hi, f) - Math.min(lo, f) > MAX_SPAN) continue;
        if (!allowed(i, f)) continue;
        frets[i] = f;
        walk(i + 1, f > 0 ? Math.min(lo, f) : lo, f > 0 ? Math.max(hi, f) : hi);
      }
      frets[i] = -1;
    };
    for (let b = 0; b <= 2; b++) {   // root in the bass on string 6, 5 or 4
      for (let fb = 0; fb <= MAX_FRET; fb++) {
        if (mod12(midiAt(b, fb)) !== root) continue;
        frets.fill(-1);
        frets[b] = fb;
        walk(b + 1, fb > 0 ? fb : Infinity, fb > 0 ? fb : -Infinity);
      }
    }
  }
  return [...found.values()].sort((x, y) => y.score - x.score);
}

function evaluate(
  frets: number[], root: number, v: Variant, analyzed: Set<number>,
  drone: number[], standard: number[], standardClosed: boolean,
): { name: string; score: number } | null {
  const notes = frets.flatMap((f, i) => (f < 0 ? [] : [{ i, f, midi: midiAt(i, f), iv: mod12(midiAt(i, f) - root), drone: f === drone[i] }]));
  if (notes.length < 4) return null;
  const ivs = new Set(notes.map((n) => n.iv));
  const ringing = new Set(notes.filter((n) => n.f === 0 || n.drone).map((n) => n.iv));   // open or drone strings
  const droneUsed = notes.filter((n) => n.drone).length;
  if (!droneUsed) return null;
  if (!v.required.every((i) => ivs.has(i))) return null;
  if (v.sus && !notes.some((n) => n.drone && n.iv === 5)) return null;   // the 3rd goes only because the drone's 4th clashes
  for (const n of notes) {
    if (v.tones.has(n.iv)) continue;
    if (n.f === 0 && n.i < 3) return null;                       // colours ring from the top three strings only
    if (n.f > 0 && !n.drone && !ringing.has(n.iv)) return null;  // the hand may double a ringing colour, never add one
  }
  const added = [...ivs].filter((i) => !analyzed.has(i)).length;
  if (added > MAX_COLORS) return null;
  const suffix = chordSuffix(ivs);
  if (suffix == null) return null;

  const fretted = frets.filter((f) => f > 0);
  const span = fretted.length ? Math.max(...fretted) - Math.min(...fretted) : 0;
  if (span > MAX_SPAN) return null;
  const fingers = fingersNeeded(frets);
  if (fingers == null || fingers > MAX_FINGERS) return null;
  // the fretting hand is one block: no open string between two of its (non-drone) notes
  const hand = frets.flatMap((f, i) => (f > 0 && f !== drone[i] ? [i] : []));
  if (hand.length && frets.slice(hand[0], hand[hand.length - 1] + 1).includes(0)) return null;
  const midis = notes.map((n) => n.midi).sort((x, y) => x - y);
  if (midis.some((n, k) => k > 0 && n - midis[k - 1] >= 1 && n - midis[k - 1] <= 2 && midis[k - 1] < MUDDY_BELOW)) return null;
  if (frets.join(",") === standard.join(",")) return null;
  // nothing new: worth suggesting only when it replaces a barre/closed shape or brings in the drone
  if (!added && !standardClosed && frets.every((f, i) => drone[i] < 0 || f !== drone[i] || standard[i] === f)) return null;

  const handColors = notes.filter((n) => n.f > 0 && !n.drone && !v.tones.has(n.iv)).length;
  const score = 4 * droneUsed
    + (added ? 2 + 0.5 * (added - 1) : 0)
    - (v.sus ? 2 : 0)
    + (ivs.has(7) ? 2 : 0)
    - 0.5 * handColors
    + 0.25 * notes.length
    - 0.75 * fingers
    - 0.5 * span
    - 0.15 * (fretted.length ? fretted.reduce((a, f) => a + f, 0) / fretted.length : 0)
    - (frets[5] < 0 ? 1 : 0);
  return { name: NOTE_NAMES[root] + suffix, score };
}

function droneFits(drone: number[], scale: Set<number>): boolean {
  return drone.every((f, i) => f < 0 || scale.has(mod12(midiAt(i, f))));
}

/** drones whose notes all lie in the key's (pentatonic) scale */
export function dronesForKey(key: Key): DroneId[] {
  const scale = scalePitchClasses(key);
  return (Object.keys(DRONES) as DroneId[]).filter((id) => droneFits(DRONES[id], scale));
}

// how many drone notes are the key's tonic or 5th — the steadiest notes to hold through a song
function droneStability(id: DroneId, key: Key): number {
  const t = pitchClass(key.tonic);
  return DRONES[id].filter((f, i) => f >= 0 && [t, mod12(t + 7)].includes(mod12(midiAt(i, f)))).length;
}

interface DronePick { id: DroneId; ranked: Map<string, Ranked[]> }

function bestDrone(labels: string[], key: Key): DronePick | null {
  let best: (DronePick & { covered: number; stable: number; total: number }) | null = null;
  for (const id of dronesForKey(key)) {
    const ranked = new Map([...new Set(labels)].map((l) => [l, rank(l, key, id)]));
    const tops = [...ranked.values()].flatMap((r) => r.slice(0, 1));
    const c = { id, ranked, covered: tops.length, stable: droneStability(id, key), total: tops.reduce((a, t) => a + t.score, 0) };
    if (!best || c.covered > best.covered || (c.covered === best.covered
      && (c.stable > best.stable || (c.stable === best.stable && c.total > best.total)))) best = c;
  }
  return best && best.covered > 0 ? best : null;
}

/** one drone for a whole chart, so the same notes ring through every chord: the one that gives the most chords
 *  a suggestion, then the steadier one, then the better total. null when no chord gets a suggestion. */
export function pickDrone(labels: string[], key: Key): DroneId | null {
  return bestDrone(labels, key)?.id ?? null;
}

/** suggested voicings for a chord shape (sharp-canonical label, after chordView's shift) in the shape key, best first.
 *  Without a drone, the one pickDrone() would choose for this chord alone. Empty when nothing colourful fits. */
export function suggestVoicings(label: string, key: Key, opts: { drone?: DroneId; limit?: number } = {}): Voicing[] {
  const ranked = opts.drone ? rank(label, key, opts.drone) : bestDrone([label], key)?.ranked.get(label) ?? [];
  return ranked.slice(0, opts.limit ?? DEFAULT_LIMIT).map(({ frets, name }) => ({ frets, name }));
}
